<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactsAPI from 'dashboard/api/contacts';
import ImoveisAPI from 'dashboard/api/imoveis';
import VisitaAPI from 'dashboard/api/visitas';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SurveyFormsAPI from 'dashboard/api/surveyForms';
import { FIELD_INPUT_CLASS, LEASE_BOUND_OPERATIONAL_FLOWS } from '../constants';

const props = defineProps({
  show: { type: Boolean, default: false },
  form: { type: Object, default: null },
});

const emit = defineEmits(['close']);
const { t, locale } = useI18n();

const propertyQuery = ref('');
const contactQuery = ref('');
const propertyResults = ref([]);
const contactResults = ref([]);
const propertySearchAttempted = ref(false);
const contactSearchAttempted = ref(false);
const activePropertyResultIndex = ref(-1);
const activeContactResultIndex = ref(-1);
const selectedProperty = ref(null);
const selectedContact = ref(null);
const visitOptions = ref([]);
const selectedVisit = ref(null);
const handoverOptions = ref([]);
const selectedHandover = ref(null);
const contractOptions = ref([]);
const selectedContractOffer = ref(null);
const leaseOptions = ref([]);
const selectedLease = ref(null);
const links = ref([]);
const generatedLink = ref(null);
const deliveryChannel = ref('');
const deliveryAvailability = ref([]);
const selectedContactInboxId = ref('');
const isLoadingProperties = ref(false);
const isLoadingContacts = ref(false);
const isLoadingVisits = ref(false);
const isLoadingLinks = ref(false);
const isLoadingDeliveryOptions = ref(false);
const hasLoadedDeliveryOptions = ref(false);
const isLoadingHandoverOptions = ref(false);
const hasLoadedHandoverOptions = ref(false);
const isLoadingContractOptions = ref(false);
const hasLoadedContractOptions = ref(false);
const isLoadingLeaseOptions = ref(false);
const hasLoadedLeaseOptions = ref(false);
const isGenerating = ref(false);
const revokingLinkId = ref(null);
const propertyError = ref(false);
const contactError = ref(false);
const visitOptionsError = ref('');
const historyError = ref('');
const deliveryOptionsError = ref('');
const handoverOptionsError = ref('');
const contractOptionsError = ref('');
const leaseOptionsError = ref('');
const issueError = ref('');
const reissueNotice = ref('');
const linkPendingRevocation = ref(null);
const revokeError = ref('');
let propertyTimer = null;
let contactTimer = null;
let propertyRequestId = 0;
let contactRequestId = 0;
let visitsRequestId = 0;
let linksRequestId = 0;
let deliveryOptionsRequestId = 0;
let handoverOptionsRequestId = 0;
let contractOptionsRequestId = 0;
let leaseOptionsRequestId = 0;
let issueRequestId = 0;
let deliveryOptionsController = null;
let handoverOptionsController = null;
let contractOptionsController = null;
let leaseOptionsController = null;

const DELIVERY_CHANNELS = ['whatsapp', 'email'];
const DELIVERY_CONFIRMED_STATUSES = ['sent', 'delivered'];
const DELIVERY_PENDING_STATUSES = [
  'queued',
  'pending',
  'processing',
  'sending',
  'held',
];
const DELIVERY_RETRYABLE_STATUSES = ['failed', 'suppressed', 'unavailable'];
const PROPERTY_OPTIONAL_OPERATIONAL_FLOWS = [
  'listing_intake',
  'owner_onboarding',
  'buyer_qualification',
  'tenant_qualification',
];

const propertyRequired = computed(
  () =>
    !PROPERTY_OPTIONAL_OPERATIONAL_FLOWS.includes(
      props.form?.settings?.operational_flow
    )
);

const visitFeedbackRequired = computed(
  () => props.form?.settings?.operational_flow === 'visit_feedback'
);

const propertySearchEmpty = computed(
  () =>
    propertySearchAttempted.value &&
    !isLoadingProperties.value &&
    !propertyError.value &&
    !propertyResults.value.length
);

const contactSearchEmpty = computed(
  () =>
    contactSearchAttempted.value &&
    !isLoadingContacts.value &&
    !contactError.value &&
    !contactResults.value.length
);

const handoverRequired = computed(
  () => props.form?.settings?.operational_flow === 'post_sale_follow_up'
);

const contractSignatureRequired = computed(
  () => props.form?.settings?.operational_flow === 'contract_signature'
);

const leaseRequired = computed(() =>
  LEASE_BOUND_OPERATIONAL_FLOWS.includes(props.form?.settings?.operational_flow)
);

const inspectionLeaseRequired = computed(
  () => props.form?.settings?.operational_flow === 'inspection'
);

const leaseCopy = computed(() =>
  inspectionLeaseRequired.value
    ? {
        label: t('SURVEYS.FRIENDLY_LINK.INSPECTION_LEASE'),
        required: t('SURVEYS.FRIENDLY_LINK.INSPECTION_LEASE_REQUIRED'),
        placeholder: t('SURVEYS.FRIENDLY_LINK.INSPECTION_LEASE_PLACEHOLDER'),
        help: t('SURVEYS.FRIENDLY_LINK.INSPECTION_LEASE_HELP'),
        loading: t('SURVEYS.FRIENDLY_LINK.INSPECTION_LEASE_LOADING'),
        error: t('SURVEYS.FRIENDLY_LINK.INSPECTION_LEASE_ERROR'),
        retry: t('SURVEYS.FRIENDLY_LINK.RETRY_INSPECTION_LEASE'),
        empty: t('SURVEYS.FRIENDLY_LINK.INSPECTION_LEASE_EMPTY'),
      }
    : {
        label: t('SURVEYS.FRIENDLY_LINK.MAINTENANCE_LEASE'),
        required: t('SURVEYS.FRIENDLY_LINK.MAINTENANCE_LEASE_REQUIRED'),
        placeholder: t('SURVEYS.FRIENDLY_LINK.MAINTENANCE_LEASE_PLACEHOLDER'),
        help: t('SURVEYS.FRIENDLY_LINK.MAINTENANCE_LEASE_HELP'),
        loading: t('SURVEYS.FRIENDLY_LINK.MAINTENANCE_LEASE_LOADING'),
        error: t('SURVEYS.FRIENDLY_LINK.MAINTENANCE_LEASE_ERROR'),
        retry: t('SURVEYS.FRIENDLY_LINK.RETRY_MAINTENANCE_LEASE'),
        empty: t('SURVEYS.FRIENDLY_LINK.MAINTENANCE_LEASE_EMPTY'),
      }
);

const selectedSummary = computed(() => {
  if (!selectedContact.value) return '';
  if (!propertyRequired.value) return selectedContact.value.name;
  if (!selectedProperty.value) return '';
  if (visitFeedbackRequired.value && !selectedVisit.value) return '';

  if (leaseRequired.value && !selectedLease.value) return '';
  if (handoverRequired.value && !selectedHandover.value) return '';
  if (contractSignatureRequired.value && !selectedContractOffer.value) {
    return '';
  }

  if (handoverRequired.value) {
    return `${selectedProperty.value.label} · ${selectedContact.value.name} · ${selectedHandover.value.label}`;
  }

  if (contractSignatureRequired.value) {
    return `${selectedProperty.value.label} · ${selectedContact.value.name} · ${selectedContractOffer.value.label}`;
  }

  if (leaseRequired.value) {
    return `${selectedProperty.value.label} · ${selectedContact.value.name} · ${selectedLease.value.label}`;
  }

  if (visitFeedbackRequired.value) {
    return `${selectedProperty.value.label} · ${selectedContact.value.name} · ${selectedVisit.value.label}`;
  }

  return `${selectedProperty.value.label} · ${selectedContact.value.name}`;
});

const hasSelectedReferences = computed(() =>
  Boolean(
    selectedContact.value &&
      (!propertyRequired.value || selectedProperty.value) &&
      (!visitFeedbackRequired.value || selectedVisit.value) &&
      (!leaseRequired.value || selectedLease.value) &&
      (!handoverRequired.value || selectedHandover.value) &&
      (!contractSignatureRequired.value || selectedContractOffer.value)
  )
);

const deliveryAvailabilityByChannel = computed(() =>
  Object.fromEntries(
    deliveryAvailability.value.map(option => [option.channel, option])
  )
);

const deliveryChannelOption = channel => {
  const availability = deliveryAvailabilityByChannel.value[channel];
  const unavailable =
    hasLoadedDeliveryOptions.value && availability?.available !== true;

  return {
    available: availability?.available === true,
    disabled:
      !hasSelectedReferences.value ||
      isLoadingDeliveryOptions.value ||
      Boolean(deliveryOptionsError.value) ||
      unavailable,
    routes: availability?.routes || [],
    unavailableReason: unavailable ? availability?.reason || 'unavailable' : '',
  };
};

const deliveryOptions = computed(() => [
  {
    value: '',
    icon: 'i-lucide-copy',
    label: t('SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS.GENERATE_ONLY.TITLE'),
    description: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS.GENERATE_ONLY.DESCRIPTION'
    ),
  },
  {
    value: 'whatsapp',
    icon: 'i-lucide-message-circle',
    label: t('SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS.WHATSAPP.TITLE'),
    description: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS.WHATSAPP.DESCRIPTION'
    ),
    ...deliveryChannelOption('whatsapp'),
  },
  {
    value: 'email',
    icon: 'i-lucide-mail',
    label: t('SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS.EMAIL.TITLE'),
    description: t('SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS.EMAIL.DESCRIPTION'),
    ...deliveryChannelOption('email'),
  },
]);

const selectedDeliveryOption = computed(() =>
  deliveryChannel.value
    ? deliveryAvailabilityByChannel.value[deliveryChannel.value] || null
    : null
);

const selectedDeliveryRoutes = computed(() =>
  (selectedDeliveryOption.value?.routes || []).filter(
    route => route.available !== false
  )
);

const requiresContactInboxSelection = computed(
  () => selectedDeliveryRoutes.value.length > 1
);

const hasSelectedContactInbox = computed(() =>
  selectedDeliveryRoutes.value.some(
    route =>
      String(route.contactInboxId) === String(selectedContactInboxId.value)
  )
);

const contactInboxIdForDeliveryChannel = channel => {
  const routes = (
    deliveryAvailabilityByChannel.value[channel]?.routes || []
  ).filter(route => route.available !== false);
  if (routes.length === 1) return routes[0].contactInboxId;
  if (
    routes.some(
      route =>
        String(route.contactInboxId) === String(selectedContactInboxId.value)
    )
  ) {
    return selectedContactInboxId.value;
  }
  return null;
};

const canSendThroughChannel = channel => {
  const option = deliveryAvailabilityByChannel.value[channel];
  if (
    !hasSelectedReferences.value ||
    isLoadingDeliveryOptions.value ||
    deliveryOptionsError.value ||
    !hasLoadedDeliveryOptions.value ||
    !option?.available
  ) {
    return false;
  }

  return (
    option.routes.length < 2 ||
    Boolean(contactInboxIdForDeliveryChannel(channel))
  );
};

const canGenerate = computed(() =>
  deliveryChannel.value
    ? canSendThroughChannel(deliveryChannel.value)
    : hasSelectedReferences.value
);

const formatDateTime = value => {
  if (!value) return t('SURVEYS.FRIENDLY_LINK.DATE_UNAVAILABLE');
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
};

const statusClass = status =>
  ({
    active: 'bg-lais-success/10 text-lais-success',
    expired: 'bg-lais-warning/10 text-lais-warning',
    revoked: 'bg-lais-danger-50 text-lais-danger',
  })[status] || 'bg-lais-bg-soft text-lais-text-muted';

const statusLabel = status =>
  ({
    active: t('SURVEYS.FRIENDLY_LINK.STATUS_ACTIVE'),
    expired: t('SURVEYS.FRIENDLY_LINK.STATUS_EXPIRED'),
    revoked: t('SURVEYS.FRIENDLY_LINK.STATUS_REVOKED'),
  })[status] || status;

const deliveryDetails = link => {
  const delivery = link?.delivery || {};
  const status = (delivery.status || link?.delivery_status || 'not_requested')
    .toString()
    .toLowerCase();
  const requested =
    delivery.requested ??
    link?.delivery_requested ??
    status !== 'not_requested';
  const confirmed =
    DELIVERY_CONFIRMED_STATUSES.includes(status) &&
    delivery.confirmed !== false &&
    link?.delivery_confirmed !== false;

  return {
    status,
    channel: delivery.channel || link?.delivery_channel || '',
    updatedAt: delivery.updated_at || link?.delivery_updated_at || '',
    reason: delivery.reason || link?.delivery_reason || '',
    requested: Boolean(requested),
    confirmed,
  };
};

const deliveryOutcome = link => {
  const { requested, confirmed } = deliveryDetails(link);

  if (confirmed) return 'confirmed';
  return requested ? 'unconfirmed' : 'not_requested';
};

const isDeliveryPending = link =>
  DELIVERY_PENDING_STATUSES.includes(deliveryDetails(link).status);

const deliveryResultClass = link => {
  const outcome = deliveryOutcome(link);

  if (outcome === 'confirmed') {
    return 'border-lais-success/25 bg-lais-success/5';
  }
  if (outcome === 'not_requested') {
    return 'border-lais-primary/20 bg-lais-primary-50/30';
  }
  if (isDeliveryPending(link)) {
    return 'border-lais-warning/25 bg-lais-warning-50/50';
  }

  return 'border-lais-danger/25 bg-lais-danger-50/50';
};

const deliveryResultIconClass = link => {
  const outcome = deliveryOutcome(link);

  if (outcome === 'confirmed') return 'bg-lais-success i-lucide-check';
  if (outcome === 'not_requested') return 'bg-lais-primary i-lucide-link';
  if (isDeliveryPending(link)) return 'bg-lais-warning i-lucide-clock-3';

  return 'bg-lais-danger i-lucide-circle-alert';
};

const deliveryResultTitle = link => {
  const outcome = deliveryOutcome(link);

  if (outcome === 'confirmed') {
    return t('SURVEYS.FRIENDLY_LINK.DELIVERY_CONFIRMED_TITLE');
  }
  if (outcome === 'not_requested') return t('SURVEYS.FRIENDLY_LINK.READY');
  if (isDeliveryPending(link)) {
    return t('SURVEYS.FRIENDLY_LINK.DELIVERY_PENDING_TITLE');
  }

  return t('SURVEYS.FRIENDLY_LINK.DELIVERY_UNCONFIRMED_TITLE');
};

const deliveryResultDescription = link => {
  const outcome = deliveryOutcome(link);

  if (outcome === 'confirmed') {
    return t('SURVEYS.FRIENDLY_LINK.DELIVERY_CONFIRMED_DESCRIPTION');
  }
  if (outcome === 'not_requested') {
    return t('SURVEYS.FRIENDLY_LINK.READY_DESCRIPTION');
  }
  if (isDeliveryPending(link)) {
    return t('SURVEYS.FRIENDLY_LINK.DELIVERY_PENDING_DESCRIPTION');
  }

  return t('SURVEYS.FRIENDLY_LINK.DELIVERY_UNCONFIRMED_DESCRIPTION');
};

const deliveryStatusClass = status => {
  const normalizedStatus = status?.toLowerCase();

  if (DELIVERY_CONFIRMED_STATUSES.includes(normalizedStatus)) {
    return 'bg-lais-success/10 text-lais-success';
  }
  if (DELIVERY_PENDING_STATUSES.includes(normalizedStatus)) {
    return 'bg-lais-warning/10 text-lais-warning';
  }
  if (
    ['failed', 'error', 'suppressed', 'unavailable', 'unknown'].includes(
      normalizedStatus
    )
  ) {
    return 'bg-lais-danger-50 text-lais-danger';
  }

  return 'bg-lais-bg-soft text-lais-text-muted';
};

const deliveryStatusLabel = status =>
  ({
    sent: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.SENT'),
    delivered: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.DELIVERED'),
    queued: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.QUEUED'),
    pending: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.PENDING'),
    processing: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.PENDING'),
    sending: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.PENDING'),
    failed: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.FAILED'),
    error: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.FAILED'),
    held: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.HELD'),
    suppressed: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.SUPPRESSED'),
    unknown: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.UNKNOWN'),
    unavailable: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.UNAVAILABLE'),
    not_requested: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.NOT_REQUESTED'),
    not_sent: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.NOT_REQUESTED'),
    skipped: t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.NOT_REQUESTED'),
  })[status?.toLowerCase()] ||
  t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS.UNAVAILABLE');

const deliveryChannelLabel = channel =>
  ({
    whatsapp: t('SURVEYS.FRIENDLY_LINK.DELIVERY_CHANNEL.WHATSAPP'),
    email: t('SURVEYS.FRIENDLY_LINK.DELIVERY_CHANNEL.EMAIL'),
  })[channel] || t('SURVEYS.FRIENDLY_LINK.DELIVERY_CHANNEL.NONE');

const deliveryReasonLabel = reason =>
  ({
    consent: t('SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.CONSENT'),
    frequency_cap: t('SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.FREQUENCY_CAP'),
    outside_business_hours: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.OUTSIDE_BUSINESS_HOURS'
    ),
    template: t('SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.TEMPLATE'),
    channel_unavailable: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.CHANNEL_UNAVAILABLE'
    ),
    channel_reauthorization_required: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.CHANNEL_REAUTHORIZATION_REQUIRED'
    ),
    channel_readiness_unknown: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.CHANNEL_READINESS_UNKNOWN'
    ),
    channel_credentials_missing: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.CHANNEL_CREDENTIALS_MISSING'
    ),
    channel_configuration_invalid: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.CHANNEL_CONFIGURATION_INVALID'
    ),
    email_transport_unconfigured: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.EMAIL_TRANSPORT_UNCONFIGURED'
    ),
    ledger_unavailable: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.LEDGER_UNAVAILABLE'
    ),
    delivery_failed: t(
      'SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.DELIVERY_FAILED'
    ),
  })[reason] || t('SURVEYS.FRIENDLY_LINK.DELIVERY_REASONS.UNAVAILABLE');

const generateLabel = computed(() =>
  deliveryChannel.value
    ? t('SURVEYS.FRIENDLY_LINK.GENERATE_AND_SEND', {
        channel: deliveryChannelLabel(deliveryChannel.value),
      })
    : t('SURVEYS.FRIENDLY_LINK.GENERATE')
);

const supportsDeliveryRetry = link =>
  DELIVERY_RETRYABLE_STATUSES.includes(deliveryDetails(link).status) &&
  DELIVERY_CHANNELS.includes(deliveryDetails(link).channel);

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

const clearTimers = () => {
  if (propertyTimer) clearTimeout(propertyTimer);
  if (contactTimer) clearTimeout(contactTimer);
  propertyTimer = null;
  contactTimer = null;
};

const invalidatePropertySearch = () => {
  propertyRequestId += 1;
  propertyResults.value = [];
  propertySearchAttempted.value = false;
  activePropertyResultIndex.value = -1;
  propertyError.value = false;
  isLoadingProperties.value = false;
};

const invalidateContactSearch = () => {
  contactRequestId += 1;
  contactResults.value = [];
  contactSearchAttempted.value = false;
  activeContactResultIndex.value = -1;
  contactError.value = false;
  isLoadingContacts.value = false;
};

const isAbortError = error =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED';

const cancelDeliveryOptionsRequest = () => {
  deliveryOptionsRequestId += 1;
  deliveryOptionsController?.abort();
  deliveryOptionsController = null;
};

const cancelHandoverOptionsRequest = () => {
  handoverOptionsRequestId += 1;
  handoverOptionsController?.abort();
  handoverOptionsController = null;
};

const cancelContractOptionsRequest = () => {
  contractOptionsRequestId += 1;
  contractOptionsController?.abort();
  contractOptionsController = null;
};

const cancelLeaseOptionsRequest = () => {
  leaseOptionsRequestId += 1;
  leaseOptionsController?.abort();
  leaseOptionsController = null;
};

const resetDeliveryOptions = () => {
  cancelDeliveryOptionsRequest();
  deliveryAvailability.value = [];
  selectedContactInboxId.value = '';
  isLoadingDeliveryOptions.value = false;
  hasLoadedDeliveryOptions.value = false;
  deliveryOptionsError.value = '';
};

const resetHandoverOptions = () => {
  cancelHandoverOptionsRequest();
  handoverOptions.value = [];
  selectedHandover.value = null;
  isLoadingHandoverOptions.value = false;
  hasLoadedHandoverOptions.value = false;
  handoverOptionsError.value = '';
};

const resetContractOptions = () => {
  cancelContractOptionsRequest();
  contractOptions.value = [];
  selectedContractOffer.value = null;
  isLoadingContractOptions.value = false;
  hasLoadedContractOptions.value = false;
  contractOptionsError.value = '';
};

const resetLeaseOptions = () => {
  cancelLeaseOptionsRequest();
  leaseOptions.value = [];
  selectedLease.value = null;
  isLoadingLeaseOptions.value = false;
  hasLoadedLeaseOptions.value = false;
  leaseOptionsError.value = '';
};

const resetVisitOptions = () => {
  visitsRequestId += 1;
  visitOptions.value = [];
  selectedVisit.value = null;
  isLoadingVisits.value = false;
  visitOptionsError.value = '';
};

const syncSelectedInbox = () => {
  const routes = selectedDeliveryRoutes.value;
  if (routes.length === 1) {
    selectedContactInboxId.value = routes[0].contactInboxId;
    return;
  }

  if (!hasSelectedContactInbox.value) selectedContactInboxId.value = '';
};

const fetchDeliveryOptions = async () => {
  const formId = props.form?.id;
  const propertyId = selectedProperty.value?.id;
  const contactId = selectedContact.value?.id;

  if (!props.show || !formId || !contactId || !hasSelectedReferences.value) {
    resetDeliveryOptions();
    return;
  }

  cancelDeliveryOptionsRequest();
  const controller = new AbortController();
  deliveryOptionsController = controller;
  deliveryOptionsRequestId += 1;
  const requestId = deliveryOptionsRequestId;
  deliveryAvailability.value = [];
  selectedContactInboxId.value = '';
  isLoadingDeliveryOptions.value = true;
  hasLoadedDeliveryOptions.value = false;
  deliveryOptionsError.value = '';

  try {
    const { data } = await SurveyFormsAPI.friendlyLinkDeliveryOptions(
      formId,
      {
        contact_id: contactId,
        ...(propertyId ? { imovel_id: propertyId } : {}),
      },
      { signal: controller.signal }
    );
    if (requestId !== deliveryOptionsRequestId) return;

    const channels = Array.isArray(data?.data?.channels)
      ? data.data.channels
      : [];
    deliveryAvailability.value = channels
      .filter(channel => DELIVERY_CHANNELS.includes(channel?.channel))
      .map(channel => ({
        channel: channel.channel,
        available: channel.available === true,
        reason: channel.reason || null,
        routes: Array.isArray(channel.routes)
          ? channel.routes.map(route => ({
              contactInboxId: route.contact_inbox_id,
              label: route.label,
              available: route.available !== false,
              reason: route.reason || null,
            }))
          : [],
      }));
    hasLoadedDeliveryOptions.value = true;
    syncSelectedInbox();
  } catch (error) {
    if (requestId !== deliveryOptionsRequestId || isAbortError(error)) return;
    deliveryOptionsError.value = errorMessage(
      error,
      t('SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS_ERROR')
    );
  } finally {
    if (deliveryOptionsController === controller) {
      deliveryOptionsController = null;
    }
    if (requestId === deliveryOptionsRequestId) {
      isLoadingDeliveryOptions.value = false;
    }
  }
};

const fetchHandoverOptions = async () => {
  const formId = props.form?.id;
  const propertyId = selectedProperty.value?.id;
  const contactId = selectedContact.value?.id;

  if (
    !props.show ||
    !formId ||
    !handoverRequired.value ||
    !propertyId ||
    !contactId
  ) {
    resetHandoverOptions();
    return;
  }

  cancelHandoverOptionsRequest();
  const controller = new AbortController();
  handoverOptionsController = controller;
  handoverOptionsRequestId += 1;
  const requestId = handoverOptionsRequestId;
  handoverOptions.value = [];
  selectedHandover.value = null;
  isLoadingHandoverOptions.value = true;
  hasLoadedHandoverOptions.value = false;
  handoverOptionsError.value = '';

  try {
    const { data } = await SurveyFormsAPI.friendlyLinkHandoverOptions(
      formId,
      { imovel_id: propertyId, contact_id: contactId },
      { signal: controller.signal }
    );
    if (requestId !== handoverOptionsRequestId) return;

    handoverOptions.value = (data?.data?.options || [])
      .filter(option => Number(option?.id) > 0 && option?.label)
      .map(option => ({
        id: option.id,
        label: option.label,
        status: option.status || '',
        handoverType: option.handover_type || '',
        acceptedAt: option.accepted_at || '',
        warrantyEndsOn: option.warranty_ends_on || '',
      }));
    hasLoadedHandoverOptions.value = true;
  } catch (error) {
    if (requestId !== handoverOptionsRequestId || isAbortError(error)) return;
    handoverOptionsError.value = errorMessage(
      error,
      t('SURVEYS.FRIENDLY_LINK.HANDOVER_ERROR')
    );
  } finally {
    if (handoverOptionsController === controller) {
      handoverOptionsController = null;
    }
    if (requestId === handoverOptionsRequestId) {
      isLoadingHandoverOptions.value = false;
    }
  }
};

const fetchContractOptions = async () => {
  const formId = props.form?.id;
  const propertyId = selectedProperty.value?.id;
  const contactId = selectedContact.value?.id;

  if (
    !props.show ||
    !formId ||
    !contractSignatureRequired.value ||
    !propertyId ||
    !contactId
  ) {
    resetContractOptions();
    return;
  }

  cancelContractOptionsRequest();
  const controller = new AbortController();
  contractOptionsController = controller;
  contractOptionsRequestId += 1;
  const requestId = contractOptionsRequestId;
  contractOptions.value = [];
  selectedContractOffer.value = null;
  isLoadingContractOptions.value = true;
  hasLoadedContractOptions.value = false;
  contractOptionsError.value = '';

  try {
    const { data } = await SurveyFormsAPI.friendlyLinkContractOptions(
      formId,
      { imovel_id: propertyId, contact_id: contactId },
      { signal: controller.signal }
    );
    if (requestId !== contractOptionsRequestId) return;

    contractOptions.value = (data?.data?.options || [])
      .filter(option => Number(option?.id) > 0 && option?.label)
      .map(option => ({
        id: option.id,
        label: option.label,
        status: option.status || '',
        contractStatus: option.contract_status || '',
      }));
    hasLoadedContractOptions.value = true;
  } catch (error) {
    if (requestId !== contractOptionsRequestId || isAbortError(error)) return;
    contractOptionsError.value = errorMessage(
      error,
      t('SURVEYS.FRIENDLY_LINK.CONTRACT_ERROR')
    );
  } finally {
    if (contractOptionsController === controller) {
      contractOptionsController = null;
    }
    if (requestId === contractOptionsRequestId) {
      isLoadingContractOptions.value = false;
    }
  }
};

const fetchLeaseOptions = async () => {
  const formId = props.form?.id;
  const propertyId = selectedProperty.value?.id;
  const contactId = selectedContact.value?.id;

  if (
    !props.show ||
    !formId ||
    !leaseRequired.value ||
    !propertyId ||
    !contactId
  ) {
    resetLeaseOptions();
    return;
  }

  cancelLeaseOptionsRequest();
  const controller = new AbortController();
  leaseOptionsController = controller;
  leaseOptionsRequestId += 1;
  const requestId = leaseOptionsRequestId;
  leaseOptions.value = [];
  selectedLease.value = null;
  isLoadingLeaseOptions.value = true;
  hasLoadedLeaseOptions.value = false;
  leaseOptionsError.value = '';

  try {
    const requestOptions = [
      formId,
      { imovel_id: propertyId, contact_id: contactId },
      { signal: controller.signal },
    ];
    const { data } = inspectionLeaseRequired.value
      ? await SurveyFormsAPI.friendlyLinkInspectionOptions(...requestOptions)
      : await SurveyFormsAPI.friendlyLinkMaintenanceOptions(...requestOptions);
    if (requestId !== leaseOptionsRequestId) return;

    leaseOptions.value = (data?.data?.options || [])
      .filter(option => Number(option?.id) > 0 && option?.label)
      .map(option => ({
        id: option.id,
        label: option.label,
        status: option.status || '',
        leaseStatus: option.lease_status || '',
      }));
    hasLoadedLeaseOptions.value = true;
  } catch (error) {
    if (requestId !== leaseOptionsRequestId || isAbortError(error)) return;
    leaseOptionsError.value = errorMessage(error, leaseCopy.value.error);
  } finally {
    if (leaseOptionsController === controller) {
      leaseOptionsController = null;
    }
    if (requestId === leaseOptionsRequestId) {
      isLoadingLeaseOptions.value = false;
    }
  }
};

const reset = () => {
  clearTimers();
  propertyQuery.value = '';
  contactQuery.value = '';
  invalidatePropertySearch();
  invalidateContactSearch();
  selectedProperty.value = null;
  selectedContact.value = null;
  resetVisitOptions();
  resetHandoverOptions();
  resetContractOptions();
  resetLeaseOptions();
  links.value = [];
  generatedLink.value = null;
  deliveryChannel.value = '';
  resetDeliveryOptions();
  historyError.value = '';
  issueError.value = '';
  reissueNotice.value = '';
  linkPendingRevocation.value = null;
  revokeError.value = '';
  isLoadingLinks.value = false;
  isGenerating.value = false;
  linksRequestId += 1;
  handoverOptionsRequestId += 1;
  contractOptionsRequestId += 1;
  leaseOptionsRequestId += 1;
  issueRequestId += 1;
};

const reconcileGeneratedLinkDelivery = historyLinks => {
  const link = generatedLink.value;
  if (!link?.id) return;

  const matchingLink = historyLinks.find(
    historyLink => String(historyLink.id) === String(link.id)
  );
  if (!matchingLink) return;

  const delivery = link.delivery || {};
  generatedLink.value = {
    ...link,
    delivery: {
      ...delivery,
      status: matchingLink.delivery_status ?? delivery.status,
      channel: matchingLink.delivery_channel ?? delivery.channel,
      updated_at: matchingLink.delivery_updated_at ?? delivery.updated_at,
      reason: matchingLink.delivery_reason ?? delivery.reason,
      requested: matchingLink.delivery_requested ?? delivery.requested,
      confirmed: matchingLink.delivery_confirmed ?? delivery.confirmed,
    },
  };
};

const fetchLinks = async () => {
  if (!props.form?.id) return;
  linksRequestId += 1;
  const requestId = linksRequestId;
  isLoadingLinks.value = true;
  historyError.value = '';
  try {
    const { data } = await SurveyFormsAPI.friendlyLinks(props.form.id);
    if (requestId !== linksRequestId) return;
    links.value = data?.data || [];
    reconcileGeneratedLinkDelivery(links.value);
  } catch (error) {
    if (requestId !== linksRequestId) return;
    links.value = [];
    historyError.value = errorMessage(
      error,
      t('SURVEYS.FRIENDLY_LINK.HISTORY_ERROR')
    );
  } finally {
    if (requestId === linksRequestId) isLoadingLinks.value = false;
  }
};

const searchProperties = async () => {
  const query = propertyQuery.value.trim();
  propertyRequestId += 1;
  const requestId = propertyRequestId;
  propertyResults.value = [];
  propertySearchAttempted.value = false;
  activePropertyResultIndex.value = -1;
  propertyError.value = false;
  if (query.length < 2) {
    isLoadingProperties.value = false;
    return;
  }

  isLoadingProperties.value = true;
  try {
    const { data } = await ImoveisAPI.get({ q: query, page: 1, per_page: 8 });
    if (requestId !== propertyRequestId) return;
    propertyResults.value = (data?.payload || []).map(property => ({
      id: property.id,
      label:
        property.titulo ||
        property.title ||
        t('SURVEYS.FRIENDLY_LINK.PROPERTY_FALLBACK', { id: property.id }),
      subtitle: [property.bairro, property.cidade, property.uf]
        .filter(Boolean)
        .join(' · '),
      detail: [property.tipo, property.quartos && `${property.quartos} qts`]
        .filter(Boolean)
        .join(' · '),
    }));
    propertySearchAttempted.value = true;
  } catch {
    if (requestId === propertyRequestId) {
      propertyResults.value = [];
      propertyError.value = true;
    }
  } finally {
    if (requestId === propertyRequestId) isLoadingProperties.value = false;
  }
};

const fetchVisitOptions = async () => {
  if (
    !visitFeedbackRequired.value ||
    !selectedProperty.value?.id ||
    !selectedContact.value?.id
  ) {
    resetVisitOptions();
    return;
  }

  visitsRequestId += 1;
  const requestId = visitsRequestId;
  isLoadingVisits.value = true;
  visitOptionsError.value = '';
  selectedVisit.value = null;
  try {
    const { data } = await VisitaAPI.get({
      imovel_id: selectedProperty.value.id,
      contact_id: selectedContact.value.id,
      page: 1,
      per_page: 50,
    });
    if (requestId !== visitsRequestId) return;

    visitOptions.value = (data?.payload || []).map(visit => {
      const scheduledAt = visit.scheduledAt || visit.scheduled_at;
      const date = scheduledAt
        ? new Intl.DateTimeFormat(locale.value === 'en' ? 'en-US' : 'pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
          }).format(new Date(scheduledAt))
        : t('SURVEYS.FRIENDLY_LINK.VISIT_DATE_UNAVAILABLE');
      return {
        id: visit.id,
        label: `${t('SURVEYS.FRIENDLY_LINK.VISIT_OPTION')} #${visit.id}`,
        detail: date,
      };
    });
  } catch (error) {
    if (requestId !== visitsRequestId) return;
    visitOptions.value = [];
    visitOptionsError.value = errorMessage(
      error,
      t('SURVEYS.FRIENDLY_LINK.VISIT_ERROR')
    );
  } finally {
    if (requestId === visitsRequestId) isLoadingVisits.value = false;
  }
};

const visitOptionLabel = visit =>
  [visit?.label, visit?.detail].filter(Boolean).join(' · ');

const searchContacts = async () => {
  const query = contactQuery.value.trim();
  contactRequestId += 1;
  const requestId = contactRequestId;
  contactResults.value = [];
  contactSearchAttempted.value = false;
  activeContactResultIndex.value = -1;
  contactError.value = false;
  if (query.length < 2) {
    isLoadingContacts.value = false;
    return;
  }

  isLoadingContacts.value = true;
  try {
    const { data } = await ContactsAPI.search(query, 1, 'recent_activity');
    if (requestId !== contactRequestId) return;
    contactResults.value = (data?.payload || []).map(contact => ({
      id: contact.id,
      name: contact.name || t('SURVEYS.FRIENDLY_LINK.CONTACT_FALLBACK'),
      subtitle: [contact.email, contact.phone_number]
        .filter(Boolean)
        .join(' · '),
    }));
    contactSearchAttempted.value = true;
  } catch {
    if (requestId === contactRequestId) {
      contactResults.value = [];
      contactError.value = true;
    }
  } finally {
    if (requestId === contactRequestId) isLoadingContacts.value = false;
  }
};

const schedulePropertySearch = () => {
  if (propertyTimer) clearTimeout(propertyTimer);
  propertyTimer = setTimeout(searchProperties, 280);
};

const onPropertyInput = () => {
  invalidatePropertySearch();
  if (
    selectedProperty.value &&
    propertyQuery.value !== selectedProperty.value.label
  ) {
    selectedProperty.value = null;
    resetVisitOptions();
    resetHandoverOptions();
    resetContractOptions();
    resetLeaseOptions();
  }
  schedulePropertySearch();
};

const scheduleContactSearch = () => {
  if (contactTimer) clearTimeout(contactTimer);
  contactTimer = setTimeout(searchContacts, 280);
};

const onContactInput = () => {
  invalidateContactSearch();
  if (
    selectedContact.value &&
    contactQuery.value !== selectedContact.value.name
  ) {
    selectedContact.value = null;
    resetVisitOptions();
    resetHandoverOptions();
    resetContractOptions();
    resetLeaseOptions();
  }
  scheduleContactSearch();
};

const chooseProperty = property => {
  invalidatePropertySearch();
  selectedProperty.value = property;
  propertyQuery.value = property.label;
  resetHandoverOptions();
  resetContractOptions();
  resetLeaseOptions();
  resetVisitOptions();
};

const chooseContact = contact => {
  invalidateContactSearch();
  selectedContact.value = contact;
  contactQuery.value = contact.name;
  resetHandoverOptions();
  resetContractOptions();
  resetLeaseOptions();
  resetVisitOptions();
};

const clearProperty = () => {
  invalidatePropertySearch();
  selectedProperty.value = null;
  propertyQuery.value = '';
  resetHandoverOptions();
  resetContractOptions();
  resetLeaseOptions();
  resetVisitOptions();
};

const clearContact = () => {
  invalidateContactSearch();
  selectedContact.value = null;
  contactQuery.value = '';
  resetHandoverOptions();
  resetContractOptions();
  resetLeaseOptions();
  resetVisitOptions();
};

const navigateSearchResults = (
  event,
  results,
  activeResultIndex,
  chooseResult,
  dismissResults
) => {
  if (event.key === 'Escape') {
    dismissResults();
    return;
  }

  if (!results.value.length) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    activeResultIndex.value =
      (activeResultIndex.value + 1) % results.value.length;
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    activeResultIndex.value =
      (activeResultIndex.value - 1 + results.value.length) %
      results.value.length;
    return;
  }

  if (event.key === 'Enter' && activeResultIndex.value >= 0) {
    event.preventDefault();
    chooseResult(results.value[activeResultIndex.value]);
  }
};

const dismissPropertyResults = () => {
  propertyResults.value = [];
  activePropertyResultIndex.value = -1;
};

const dismissContactResults = () => {
  contactResults.value = [];
  activeContactResultIndex.value = -1;
};

const onPropertyKeydown = event =>
  navigateSearchResults(
    event,
    propertyResults,
    activePropertyResultIndex,
    chooseProperty,
    dismissPropertyResults
  );

const onContactKeydown = event =>
  navigateSearchResults(
    event,
    contactResults,
    activeContactResultIndex,
    chooseContact,
    dismissContactResults
  );

const generate = async channel => {
  if (!props.form?.id || isGenerating.value) return;
  const requestedChannel = DELIVERY_CHANNELS.includes(channel)
    ? channel
    : deliveryChannel.value;
  if (
    !hasSelectedReferences.value ||
    (requestedChannel && !canSendThroughChannel(requestedChannel))
  ) {
    return;
  }

  issueRequestId += 1;
  const requestId = issueRequestId;
  isGenerating.value = true;
  issueError.value = '';
  reissueNotice.value = '';
  try {
    const friendlyLink = {
      contact_id: selectedContact.value.id,
      ...(selectedProperty.value?.id
        ? { imovel_id: selectedProperty.value.id }
        : {}),
      ...(selectedVisit.value?.id ? { visita_id: selectedVisit.value.id } : {}),
      ...(selectedHandover.value?.id
        ? { property_handover_id: selectedHandover.value.id }
        : {}),
      ...(selectedContractOffer.value?.id
        ? { property_offer_id: selectedContractOffer.value.id }
        : {}),
      ...(selectedLease.value?.id ? { lease_id: selectedLease.value.id } : {}),
    };
    if (requestedChannel) {
      friendlyLink.delivery_channel = requestedChannel;
      const contactInboxId = contactInboxIdForDeliveryChannel(requestedChannel);
      if (contactInboxId !== null) {
        friendlyLink.contact_inbox_id = contactInboxId;
      }
    }

    const { data } = await SurveyFormsAPI.friendlyLink(props.form.id, {
      friendly_link: friendlyLink,
    });
    if (requestId !== issueRequestId) return;
    generatedLink.value = data?.data || null;
    await fetchLinks();
  } catch (error) {
    if (requestId !== issueRequestId) return;
    issueError.value = errorMessage(error, t('SURVEYS.FRIENDLY_LINK.ERROR'));
    useAlert(issueError.value);
  } finally {
    if (requestId === issueRequestId) isGenerating.value = false;
  }
};

const retryGeneratedDelivery = () => {
  const channel = deliveryDetails(generatedLink.value).channel;
  if (!DELIVERY_CHANNELS.includes(channel)) return;

  deliveryChannel.value = channel;
  if (canSendThroughChannel(channel)) generate(channel);
};

const prepareReissue = link => {
  const channel = deliveryDetails(link).channel;
  if (!DELIVERY_CHANNELS.includes(channel)) return;

  deliveryChannel.value = channel;
  issueError.value = '';
  reissueNotice.value = channel;
};

const copy = async value => {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    useAlert(t('SURVEYS.FRIENDLY_LINK.COPIED'));
  } catch {
    useAlert(t('SURVEYS.FRIENDLY_LINK.COPY_ERROR'));
  }
};

const askRevoke = link => {
  linkPendingRevocation.value = link;
  revokeError.value = '';
};

const closeRevoke = () => {
  if (revokingLinkId.value) return;
  linkPendingRevocation.value = null;
  revokeError.value = '';
};

const revoke = async () => {
  const link = linkPendingRevocation.value;
  if (!props.form?.id || !link?.id) return;
  revokingLinkId.value = link.id;
  revokeError.value = '';
  try {
    await SurveyFormsAPI.revokeFriendlyLink(props.form.id, link.id);
    await fetchLinks();
    if (generatedLink.value?.id === link.id) generatedLink.value = null;
    linkPendingRevocation.value = null;
    useAlert(t('SURVEYS.FRIENDLY_LINK.REVOKED'));
  } catch (error) {
    revokeError.value = errorMessage(
      error,
      t('SURVEYS.FRIENDLY_LINK.REVOKE_ERROR')
    );
    useAlert(revokeError.value);
  } finally {
    revokingLinkId.value = null;
  }
};

watch(deliveryChannel, () => {
  syncSelectedInbox();
});

watch(
  () => [
    props.show,
    props.form?.settings?.operational_flow,
    selectedProperty.value?.id,
    selectedContact.value?.id,
  ],
  ([isOpen, operationalFlow, propertyId, contactId]) => {
    if (
      !isOpen ||
      operationalFlow !== 'visit_feedback' ||
      !propertyId ||
      !contactId
    ) {
      resetVisitOptions();
      return;
    }

    fetchVisitOptions();
  }
);

watch(
  () => [
    props.show,
    props.form?.id,
    selectedProperty.value?.id,
    selectedContact.value?.id,
    handoverRequired.value,
  ],
  ([isOpen, formId, propertyId, contactId, requiresHandover]) => {
    if (!requiresHandover) {
      resetHandoverOptions();
      return;
    }

    if (!isOpen || !formId || !propertyId || !contactId) {
      resetHandoverOptions();
      return;
    }

    fetchHandoverOptions();
  }
);

watch(
  () => [
    props.show,
    props.form?.id,
    selectedProperty.value?.id,
    selectedContact.value?.id,
    leaseRequired.value,
    props.form?.settings?.operational_flow,
  ],
  ([isOpen, formId, propertyId, contactId, requiresLease]) => {
    if (!requiresLease) {
      resetLeaseOptions();
      return;
    }

    if (!isOpen || !formId || !propertyId || !contactId) {
      resetLeaseOptions();
      return;
    }

    fetchLeaseOptions();
  }
);

watch(
  () => [
    props.show,
    props.form?.id,
    selectedProperty.value?.id,
    selectedContact.value?.id,
    contractSignatureRequired.value,
  ],
  ([isOpen, formId, propertyId, contactId, requiresContract]) => {
    if (!requiresContract) {
      resetContractOptions();
      return;
    }

    if (!isOpen || !formId || !propertyId || !contactId) {
      resetContractOptions();
      return;
    }

    fetchContractOptions();
  }
);

watch(
  () => [
    props.show,
    props.form?.id,
    selectedProperty.value?.id,
    selectedContact.value?.id,
    propertyRequired.value,
    selectedHandover.value?.id,
    handoverRequired.value,
    selectedContractOffer.value?.id,
    contractSignatureRequired.value,
    selectedLease.value?.id,
    leaseRequired.value,
    props.form?.settings?.operational_flow,
  ],
  ([isOpen, formId]) => {
    if (!isOpen || !formId || !hasSelectedReferences.value) {
      resetDeliveryOptions();
      return;
    }

    fetchDeliveryOptions();
  }
);

watch(
  () => [props.show, props.form?.id],
  ([isOpen]) => {
    reset();
    if (isOpen) fetchLinks();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  clearTimers();
  cancelDeliveryOptionsRequest();
  cancelHandoverOptionsRequest();
  cancelContractOptionsRequest();
  cancelLeaseOptionsRequest();
});
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('SURVEYS.FRIENDLY_LINK.TITLE')"
    max-width-class="max-w-[780px]"
    @close="emit('close')"
  >
    <div class="grid gap-5">
      <div
        class="rounded-lais-xl border border-lais-primary/15 bg-lais-primary-50/35 p-4 sm:p-5"
      >
        <div class="flex items-start gap-3">
          <span
            class="grid size-10 shrink-0 place-items-center rounded-lais bg-lais-primary text-white shadow-lais-soft"
          >
            <span class="i-lucide-sparkles size-5" aria-hidden="true" />
          </span>
          <div>
            <p class="text-lais-body-sm font-semibold text-lais-text">
              {{ t('SURVEYS.FRIENDLY_LINK.SUBTITLE') }}
            </p>
            <p
              class="mt-1 text-lais-body-sm leading-relaxed text-lais-text-helper"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.DESCRIPTION') }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="grid gap-4"
        :class="{
          'md:grid-cols-2': propertyRequired && !handoverRequired,
          'xl:grid-cols-3':
            handoverRequired || contractSignatureRequired || leaseRequired,
        }"
      >
        <section
          v-if="propertyRequired"
          class="relative grid gap-2"
          aria-labelledby="survey-link-property-label"
        >
          <div class="flex items-center justify-between gap-2">
            <label
              id="survey-link-property-label"
              for="survey-link-property-input"
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.PROPERTY') }}
            </label>
            <span class="text-[11px] text-lais-text-muted">{{
              t('SURVEYS.FRIENDLY_LINK.SEARCH_HINT')
            }}</span>
          </div>
          <div class="relative">
            <span
              class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lais-text-muted i-lucide-building-2 size-4"
              aria-hidden="true"
            />
            <input
              id="survey-link-property-input"
              v-model="propertyQuery"
              type="search"
              role="combobox"
              :class="FIELD_INPUT_CLASS + ' pl-9 pr-9'"
              :placeholder="t('SURVEYS.FRIENDLY_LINK.PROPERTY_PLACEHOLDER')"
              :aria-expanded="Boolean(propertyResults.length)"
              aria-autocomplete="list"
              aria-controls="survey-link-property-results"
              :aria-activedescendant="
                activePropertyResultIndex >= 0
                  ? `survey-link-property-option-${propertyResults[activePropertyResultIndex].id}`
                  : undefined
              "
              autocomplete="off"
              @input="onPropertyInput"
              @keydown="onPropertyKeydown"
            />
            <button
              v-if="selectedProperty"
              type="button"
              class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full text-lais-text-muted hover:bg-lais-bg-soft"
              :aria-label="t('SURVEYS.FRIENDLY_LINK.CLEAR')"
              @click="clearProperty"
            >
              <span class="i-lucide-x size-4" aria-hidden="true" />
            </button>
          </div>
          <div
            v-if="isLoadingProperties"
            class="flex items-center gap-2 text-lais-body-sm text-lais-text-helper"
          >
            <span
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ t('SURVEYS.FRIENDLY_LINK.SEARCHING') }}
          </div>
          <div
            v-else-if="propertyError"
            class="flex items-center justify-between gap-3 text-lais-body-sm text-lais-danger"
          >
            <span>{{ t('SURVEYS.FRIENDLY_LINK.SEARCH_ERROR') }}</span>
            <button
              type="button"
              class="shrink-0 font-semibold underline underline-offset-2 hover:text-lais-danger-dark"
              @click="searchProperties"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.RETRY_SEARCH') }}
            </button>
          </div>
          <p
            v-else-if="propertySearchEmpty"
            class="text-lais-body-sm text-lais-text-helper"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.PROPERTY_EMPTY') }}
          </p>
          <div
            v-else-if="propertyResults.length"
            id="survey-link-property-results"
            role="listbox"
            class="absolute inset-x-0 top-[72px] z-10 overflow-hidden rounded-lais border border-lais-border bg-white shadow-lais-pop"
          >
            <button
              v-for="property in propertyResults"
              :id="`survey-link-property-option-${property.id}`"
              :key="property.id"
              type="button"
              role="option"
              :aria-selected="
                propertyResults[activePropertyResultIndex]?.id === property.id
              "
              class="flex w-full items-start gap-3 border-b border-lais-border p-3 text-left last:border-0 hover:bg-lais-primary-50/40"
              :class="{
                'bg-lais-primary-50/40':
                  propertyResults[activePropertyResultIndex]?.id ===
                  property.id,
              }"
              @mouseenter="
                activePropertyResultIndex = propertyResults.indexOf(property)
              "
              @click="chooseProperty(property)"
            >
              <span
                class="grid size-8 shrink-0 place-items-center rounded-lais-sm bg-lais-bg-soft text-lais-primary i-lucide-building-2"
                aria-hidden="true"
              />
              <span class="min-w-0">
                <span
                  class="block truncate text-lais-body-sm font-semibold text-lais-text"
                >
                  {{ property.label }}
                </span>
                <span
                  class="mt-0.5 block truncate text-[12px] text-lais-text-helper"
                >
                  {{ property.subtitle || property.detail }}
                </span>
              </span>
            </button>
          </div>
          <div
            v-if="selectedProperty"
            class="flex items-center gap-2 rounded-lais-sm border border-lais-primary/20 bg-lais-primary-50/30 p-2.5 text-[12px] text-lais-text"
          >
            <span
              class="i-lucide-check-circle-2 size-4 shrink-0 text-lais-success"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate">{{ selectedProperty.label }}</span>
          </div>
        </section>

        <section
          class="relative grid gap-2"
          aria-labelledby="survey-link-contact-label"
        >
          <div class="flex items-center justify-between gap-2">
            <label
              id="survey-link-contact-label"
              for="survey-link-contact-input"
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.CONTACT') }}
            </label>
            <span class="text-[11px] text-lais-text-muted">{{
              t('SURVEYS.FRIENDLY_LINK.SEARCH_HINT')
            }}</span>
          </div>
          <div class="relative">
            <span
              class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lais-text-muted i-lucide-user-round size-4"
              aria-hidden="true"
            />
            <input
              id="survey-link-contact-input"
              v-model="contactQuery"
              type="search"
              role="combobox"
              :class="FIELD_INPUT_CLASS + ' pl-9 pr-9'"
              :placeholder="t('SURVEYS.FRIENDLY_LINK.CONTACT_PLACEHOLDER')"
              :aria-expanded="Boolean(contactResults.length)"
              aria-autocomplete="list"
              aria-controls="survey-link-contact-results"
              :aria-activedescendant="
                activeContactResultIndex >= 0
                  ? `survey-link-contact-option-${contactResults[activeContactResultIndex].id}`
                  : undefined
              "
              autocomplete="off"
              @input="onContactInput"
              @keydown="onContactKeydown"
            />
            <button
              v-if="selectedContact"
              type="button"
              class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full text-lais-text-muted hover:bg-lais-bg-soft"
              :aria-label="t('SURVEYS.FRIENDLY_LINK.CLEAR')"
              @click="clearContact"
            >
              <span class="i-lucide-x size-4" aria-hidden="true" />
            </button>
          </div>
          <div
            v-if="isLoadingContacts"
            class="flex items-center gap-2 text-lais-body-sm text-lais-text-helper"
          >
            <span
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ t('SURVEYS.FRIENDLY_LINK.SEARCHING') }}
          </div>
          <div
            v-else-if="contactError"
            class="flex items-center justify-between gap-3 text-lais-body-sm text-lais-danger"
          >
            <span>{{ t('SURVEYS.FRIENDLY_LINK.SEARCH_ERROR') }}</span>
            <button
              type="button"
              class="shrink-0 font-semibold underline underline-offset-2 hover:text-lais-danger-dark"
              @click="searchContacts"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.RETRY_SEARCH') }}
            </button>
          </div>
          <p
            v-else-if="contactSearchEmpty"
            class="text-lais-body-sm text-lais-text-helper"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.CONTACT_EMPTY') }}
          </p>
          <div
            v-else-if="contactResults.length"
            id="survey-link-contact-results"
            role="listbox"
            class="absolute inset-x-0 top-[72px] z-10 overflow-hidden rounded-lais border border-lais-border bg-white shadow-lais-pop"
          >
            <button
              v-for="contact in contactResults"
              :id="`survey-link-contact-option-${contact.id}`"
              :key="contact.id"
              type="button"
              role="option"
              :aria-selected="
                contactResults[activeContactResultIndex]?.id === contact.id
              "
              class="flex w-full items-start gap-3 border-b border-lais-border p-3 text-left last:border-0 hover:bg-lais-primary-50/40"
              :class="{
                'bg-lais-primary-50/40':
                  contactResults[activeContactResultIndex]?.id === contact.id,
              }"
              @mouseenter="
                activeContactResultIndex = contactResults.indexOf(contact)
              "
              @click="chooseContact(contact)"
            >
              <span
                class="grid size-8 shrink-0 place-items-center rounded-full bg-lais-bg-soft text-lais-primary i-lucide-user-round"
                aria-hidden="true"
              />
              <span class="min-w-0">
                <span
                  class="block truncate text-lais-body-sm font-semibold text-lais-text"
                >
                  {{ contact.name }}
                </span>
                <span
                  class="mt-0.5 block truncate text-[12px] text-lais-text-helper"
                >
                  {{
                    contact.subtitle ||
                    t('SURVEYS.FRIENDLY_LINK.CONTACT_NO_DETAILS')
                  }}
                </span>
              </span>
            </button>
          </div>
          <div
            v-if="selectedContact"
            class="flex items-center gap-2 rounded-lais-sm border border-lais-primary/20 bg-lais-primary-50/30 p-2.5 text-[12px] text-lais-text"
          >
            <span
              class="i-lucide-check-circle-2 size-4 shrink-0 text-lais-success"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate">{{ selectedContact.name }}</span>
          </div>
        </section>

        <section
          v-if="visitFeedbackRequired"
          class="grid gap-2"
          aria-labelledby="survey-link-visit-label"
          :aria-busy="isLoadingVisits"
        >
          <div class="flex items-center justify-between gap-2">
            <label
              id="survey-link-visit-label"
              for="survey-friendly-link-visit"
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.VISIT') }}
            </label>
            <span class="text-[11px] text-lais-text-muted">
              {{ t('SURVEYS.FRIENDLY_LINK.VISIT_REQUIRED') }}
            </span>
          </div>
          <select
            id="survey-friendly-link-visit"
            v-model="selectedVisit"
            :class="FIELD_INPUT_CLASS"
            :disabled="
              isLoadingVisits ||
              Boolean(visitOptionsError) ||
              !selectedProperty ||
              !selectedContact
            "
          >
            <option :value="null" disabled>
              {{ t('SURVEYS.FRIENDLY_LINK.VISIT_PLACEHOLDER') }}
            </option>
            <option
              v-for="visit in visitOptions"
              :key="visit.id"
              :value="visit"
            >
              {{ visitOptionLabel(visit) }}
            </option>
          </select>
          <p class="text-[12px] leading-relaxed text-lais-text-helper">
            {{ t('SURVEYS.FRIENDLY_LINK.VISIT_HELP') }}
          </p>
          <p
            v-if="isLoadingVisits"
            class="flex items-center gap-2 text-lais-body-sm text-lais-text-helper"
            role="status"
          >
            <span
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ t('SURVEYS.FRIENDLY_LINK.VISIT_LOADING') }}
          </p>
          <div
            v-else-if="visitOptionsError"
            class="flex flex-wrap items-center gap-2 rounded-lais-sm border border-lais-danger/25 bg-lais-danger-50 px-3 py-2 text-[12px] text-lais-danger"
            role="alert"
          >
            <span>{{ visitOptionsError }}</span>
            <button
              type="button"
              class="font-semibold underline decoration-lais-danger/50 underline-offset-2"
              @click="fetchVisitOptions"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.RETRY_SEARCH') }}
            </button>
          </div>
          <p
            v-else-if="
              !isLoadingVisits &&
              selectedProperty &&
              selectedContact &&
              !visitOptions.length
            "
            class="text-[12px] text-lais-text-helper"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.VISIT_EMPTY') }}
          </p>
        </section>

        <section
          v-if="leaseRequired"
          class="grid gap-2"
          aria-labelledby="survey-link-lease-label"
          :aria-busy="isLoadingLeaseOptions"
        >
          <div class="flex items-center justify-between gap-2">
            <label
              id="survey-link-lease-label"
              for="survey-friendly-link-lease"
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ leaseCopy.label }}
            </label>
            <span class="text-[11px] text-lais-text-muted">
              {{ leaseCopy.required }}
            </span>
          </div>
          <select
            id="survey-friendly-link-lease"
            v-model="selectedLease"
            :class="FIELD_INPUT_CLASS"
            :disabled="
              isLoadingLeaseOptions ||
              Boolean(leaseOptionsError) ||
              !selectedProperty ||
              !selectedContact
            "
            aria-describedby="survey-friendly-link-lease-help"
          >
            <option :value="null" disabled>
              {{ leaseCopy.placeholder }}
            </option>
            <option
              v-for="lease in leaseOptions"
              :key="lease.id"
              :value="lease"
            >
              {{ lease.label }}
            </option>
          </select>
          <p
            id="survey-friendly-link-lease-help"
            class="text-[12px] leading-relaxed text-lais-text-helper"
          >
            {{ leaseCopy.help }}
          </p>
          <p
            v-if="isLoadingLeaseOptions"
            class="flex items-center gap-2 text-lais-body-sm text-lais-text-helper"
            role="status"
            aria-live="polite"
          >
            <span
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ leaseCopy.loading }}
          </p>
          <div
            v-else-if="leaseOptionsError"
            class="flex flex-wrap items-center gap-2 rounded-lais-sm border border-lais-danger/25 bg-lais-danger-50 px-3 py-2 text-[12px] text-lais-danger"
            role="alert"
          >
            <span>{{ leaseOptionsError }}</span>
            <button
              type="button"
              class="font-semibold underline decoration-lais-danger/50 underline-offset-2 hover:decoration-lais-danger focus-visible:rounded-lais-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lais-danger"
              @click="fetchLeaseOptions"
            >
              {{ leaseCopy.retry }}
            </button>
          </div>
          <p
            v-else-if="
              hasLoadedLeaseOptions &&
              !leaseOptions.length &&
              selectedProperty &&
              selectedContact
            "
            class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-[12px] leading-relaxed text-lais-text"
            role="status"
          >
            {{ leaseCopy.empty }}
          </p>
          <div
            v-else-if="selectedLease"
            class="flex items-center gap-2 rounded-lais-sm border border-lais-primary/20 bg-lais-primary-50/30 p-2.5 text-[12px] text-lais-text"
          >
            <span
              class="i-lucide-key-round size-4 shrink-0 text-lais-success"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate">{{ selectedLease.label }}</span>
          </div>
        </section>

        <section
          v-if="handoverRequired"
          class="grid gap-2"
          aria-labelledby="survey-link-handover-label"
        >
          <div class="flex items-center justify-between gap-2">
            <label
              id="survey-link-handover-label"
              for="survey-friendly-link-handover"
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.HANDOVER') }}
            </label>
            <span class="text-[11px] text-lais-text-muted">
              {{ t('SURVEYS.FRIENDLY_LINK.HANDOVER_REQUIRED') }}
            </span>
          </div>
          <select
            id="survey-friendly-link-handover"
            v-model="selectedHandover"
            :class="FIELD_INPUT_CLASS"
            :disabled="
              isLoadingHandoverOptions ||
              Boolean(handoverOptionsError) ||
              !selectedProperty ||
              !selectedContact
            "
            aria-describedby="survey-friendly-link-handover-help"
          >
            <option :value="null" disabled>
              {{ t('SURVEYS.FRIENDLY_LINK.HANDOVER_PLACEHOLDER') }}
            </option>
            <option
              v-for="handover in handoverOptions"
              :key="handover.id"
              :value="handover"
            >
              {{ handover.label }}
            </option>
          </select>
          <p
            id="survey-friendly-link-handover-help"
            class="text-[12px] leading-relaxed text-lais-text-helper"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.HANDOVER_HELP') }}
          </p>
          <p
            v-if="isLoadingHandoverOptions"
            class="flex items-center gap-2 text-lais-body-sm text-lais-text-helper"
            role="status"
            aria-live="polite"
          >
            <span
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ t('SURVEYS.FRIENDLY_LINK.HANDOVER_LOADING') }}
          </p>
          <div
            v-else-if="handoverOptionsError"
            class="flex flex-wrap items-center gap-2 rounded-lais-sm border border-lais-danger/25 bg-lais-danger-50 px-3 py-2 text-[12px] text-lais-danger"
            role="alert"
          >
            <span>{{ handoverOptionsError }}</span>
            <button
              type="button"
              class="font-semibold underline decoration-lais-danger/50 underline-offset-2 hover:decoration-lais-danger focus-visible:rounded-lais-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lais-danger"
              @click="fetchHandoverOptions"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.RETRY_HANDOVER') }}
            </button>
          </div>
          <p
            v-else-if="
              hasLoadedHandoverOptions &&
              !handoverOptions.length &&
              selectedProperty &&
              selectedContact
            "
            class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-[12px] leading-relaxed text-lais-text"
            role="status"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.HANDOVER_EMPTY') }}
          </p>
          <div
            v-else-if="selectedHandover"
            class="flex items-center gap-2 rounded-lais-sm border border-lais-primary/20 bg-lais-primary-50/30 p-2.5 text-[12px] text-lais-text"
          >
            <span
              class="i-lucide-shield-check size-4 shrink-0 text-lais-success"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate">{{ selectedHandover.label }}</span>
          </div>
        </section>

        <section
          v-if="contractSignatureRequired"
          class="grid gap-2"
          aria-labelledby="survey-link-contract-label"
        >
          <div class="flex items-center justify-between gap-2">
            <label
              id="survey-link-contract-label"
              for="survey-friendly-link-contract"
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.CONTRACT_OFFER') }}
            </label>
            <span class="text-[11px] text-lais-text-muted">
              {{ t('SURVEYS.FRIENDLY_LINK.CONTRACT_REQUIRED') }}
            </span>
          </div>
          <select
            id="survey-friendly-link-contract"
            v-model="selectedContractOffer"
            :class="FIELD_INPUT_CLASS"
            :disabled="
              isLoadingContractOptions ||
              Boolean(contractOptionsError) ||
              !selectedProperty ||
              !selectedContact
            "
            aria-describedby="survey-friendly-link-contract-help"
          >
            <option :value="null" disabled>
              {{ t('SURVEYS.FRIENDLY_LINK.CONTRACT_PLACEHOLDER') }}
            </option>
            <option
              v-for="offer in contractOptions"
              :key="offer.id"
              :value="offer"
            >
              {{ offer.label }}
            </option>
          </select>
          <p
            id="survey-friendly-link-contract-help"
            class="text-[12px] leading-relaxed text-lais-text-helper"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.CONTRACT_HELP') }}
          </p>
          <p
            v-if="isLoadingContractOptions"
            class="flex items-center gap-2 text-lais-body-sm text-lais-text-helper"
            role="status"
            aria-live="polite"
          >
            <span
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ t('SURVEYS.FRIENDLY_LINK.CONTRACT_LOADING') }}
          </p>
          <div
            v-else-if="contractOptionsError"
            class="flex flex-wrap items-center gap-2 rounded-lais-sm border border-lais-danger/25 bg-lais-danger-50 px-3 py-2 text-[12px] text-lais-danger"
            role="alert"
          >
            <span>{{ contractOptionsError }}</span>
            <button
              type="button"
              class="font-semibold underline decoration-lais-danger/50 underline-offset-2 hover:decoration-lais-danger focus-visible:rounded-lais-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lais-danger"
              @click="fetchContractOptions"
            >
              {{ t('SURVEYS.FRIENDLY_LINK.RETRY_CONTRACT') }}
            </button>
          </div>
          <p
            v-else-if="
              hasLoadedContractOptions &&
              !contractOptions.length &&
              selectedProperty &&
              selectedContact
            "
            class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-[12px] leading-relaxed text-lais-text"
            role="status"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.CONTRACT_EMPTY') }}
          </p>
          <div
            v-else-if="selectedContractOffer"
            class="flex items-center gap-2 rounded-lais-sm border border-lais-primary/20 bg-lais-primary-50/30 p-2.5 text-[12px] text-lais-text"
          >
            <span
              class="i-lucide-file-signature size-4 shrink-0 text-lais-success"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate">
              {{ selectedContractOffer.label }}
            </span>
          </div>
        </section>
      </div>

      <fieldset
        class="grid gap-3"
        aria-describedby="survey-link-delivery-help"
        :aria-busy="isLoadingDeliveryOptions"
      >
        <legend
          class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
        >
          {{ t('SURVEYS.FRIENDLY_LINK.DELIVERY_LABEL') }}
        </legend>
        <p
          id="survey-link-delivery-help"
          class="text-lais-body-sm text-lais-text-helper"
        >
          {{ t('SURVEYS.FRIENDLY_LINK.DELIVERY_HELP') }}
        </p>
        <div class="grid gap-2 sm:grid-cols-3">
          <label
            v-for="option in deliveryOptions"
            :key="option.value || 'generate-only'"
            class="group flex items-start gap-3 rounded-lais border border-lais-border bg-white p-3 transition has-[:checked]:border-lais-primary has-[:checked]:bg-lais-primary-50/35 has-[:focus-visible]:shadow-lais-focus"
            :class="
              option.disabled
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:border-lais-primary/50 hover:bg-lais-primary-50/20'
            "
          >
            <input
              v-model="deliveryChannel"
              class="peer sr-only"
              type="radio"
              name="survey-friendly-link-delivery"
              :value="option.value"
              :disabled="option.disabled"
              :aria-describedby="
                option.unavailableReason
                  ? `survey-link-delivery-${option.value}-reason`
                  : undefined
              "
            />
            <span
              class="grid size-8 shrink-0 place-items-center rounded-full bg-lais-bg-soft text-lais-text-muted transition group-has-[:checked]:bg-lais-primary group-has-[:checked]:text-white"
              aria-hidden="true"
            >
              <span class="size-4" :class="option.icon" />
            </span>
            <span class="min-w-0">
              <span
                class="block text-lais-body-sm font-semibold text-lais-text"
              >
                {{ option.label }}
              </span>
              <span
                class="mt-0.5 block text-[12px] leading-relaxed text-lais-text-helper"
              >
                {{ option.description }}
              </span>
              <span
                v-if="option.unavailableReason"
                :id="`survey-link-delivery-${option.value}-reason`"
                class="mt-1 block text-[12px] leading-relaxed text-lais-danger"
              >
                {{ deliveryReasonLabel(option.unavailableReason) }}
              </span>
            </span>
          </label>
        </div>

        <p
          v-if="isLoadingDeliveryOptions"
          class="flex items-center gap-2 text-lais-body-sm text-lais-text-helper"
          role="status"
          aria-live="polite"
        >
          <span
            class="i-lucide-loader-circle size-4 animate-spin"
            aria-hidden="true"
          />
          {{ t('SURVEYS.FRIENDLY_LINK.DELIVERY_OPTIONS_LOADING') }}
        </p>
        <div
          v-else-if="deliveryOptionsError"
          class="flex flex-wrap items-center gap-3 rounded-lais-sm border border-lais-danger/25 bg-lais-danger-50 px-3 py-2 text-lais-body-sm text-lais-danger"
          role="alert"
        >
          <span>{{ deliveryOptionsError }}</span>
          <button
            type="button"
            class="font-semibold underline decoration-lais-danger/50 underline-offset-2 hover:decoration-lais-danger focus-visible:rounded-lais-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lais-danger"
            @click="fetchDeliveryOptions"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.RETRY_DELIVERY_OPTIONS') }}
          </button>
        </div>

        <div v-if="requiresContactInboxSelection" class="grid gap-1.5">
          <label
            for="survey-friendly-link-inbox"
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.INBOX_LABEL') }}
          </label>
          <select
            id="survey-friendly-link-inbox"
            v-model="selectedContactInboxId"
            :class="FIELD_INPUT_CLASS"
            aria-describedby="survey-friendly-link-inbox-help"
          >
            <option value="" disabled>
              {{ t('SURVEYS.FRIENDLY_LINK.INBOX_PLACEHOLDER') }}
            </option>
            <option
              v-for="route in selectedDeliveryRoutes"
              :key="route.contactInboxId"
              :value="route.contactInboxId"
            >
              {{ route.label }}
            </option>
          </select>
          <p
            id="survey-friendly-link-inbox-help"
            class="text-[12px] text-lais-text-helper"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.INBOX_HELP') }}
          </p>
          <p
            v-if="!hasSelectedContactInbox"
            class="text-[12px] text-lais-danger"
            role="alert"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.INBOX_REQUIRED') }}
          </p>
        </div>
      </fieldset>

      <div
        v-if="selectedSummary"
        class="flex flex-wrap items-center justify-between gap-3 rounded-lais border border-lais-border bg-lais-bg-soft px-4 py-3"
      >
        <div
          class="flex min-w-0 items-center gap-2 text-lais-body-sm text-lais-text"
        >
          <span
            class="i-lucide-link-2 size-4 shrink-0 text-lais-primary"
            aria-hidden="true"
          />
          <span class="truncate">{{ selectedSummary }}</span>
        </div>
        <LaisPrimaryButton
          size="sm"
          icon="i-lucide-wand-sparkles"
          :is-loading="isGenerating"
          :disabled="!canGenerate"
          @click="generate"
        >
          {{ generateLabel }}
        </LaisPrimaryButton>
      </div>

      <p
        v-if="reissueNotice"
        class="flex items-start gap-2 rounded-lais border border-lais-primary/20 bg-lais-primary-50/30 px-3 py-2.5 text-lais-body-sm text-lais-text"
        role="status"
        aria-live="polite"
      >
        <span
          class="i-lucide-shield-check mt-0.5 size-4 shrink-0 text-lais-primary"
          aria-hidden="true"
        />
        <span>
          {{
            t('SURVEYS.FRIENDLY_LINK.REISSUE_NOTICE', {
              channel: deliveryChannelLabel(reissueNotice),
            })
          }}
        </span>
      </p>

      <p
        v-if="issueError"
        class="flex items-start gap-2 rounded-lais border border-lais-danger/25 bg-lais-danger-50 px-3 py-2.5 text-lais-body-sm text-lais-danger"
        role="alert"
        aria-live="assertive"
      >
        <span
          class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
          aria-hidden="true"
        />
        <span>{{ issueError }}</span>
      </p>

      <section
        v-if="generatedLink"
        class="rounded-lais-xl border p-4 sm:p-5"
        :class="deliveryResultClass(generatedLink)"
        aria-live="polite"
      >
        <div class="flex items-start gap-3">
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full text-white"
            :class="deliveryResultIconClass(generatedLink)"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <p class="text-lais-body-sm font-semibold text-lais-text">
              {{ deliveryResultTitle(generatedLink) }}
            </p>
            <p class="mt-1 text-[12px] text-lais-text-helper">
              {{ deliveryResultDescription(generatedLink) }}
            </p>
            <p
              v-if="generatedLink.url"
              class="mt-1 break-all rounded-lais-sm bg-white px-3 py-2 font-mono text-[12px] text-lais-text-helper"
            >
              {{ generatedLink.url }}
            </p>
            <p class="mt-2 text-[12px] text-lais-text-helper">
              {{
                t('SURVEYS.FRIENDLY_LINK.EXPIRES', {
                  date: formatDateTime(generatedLink.expires_at),
                })
              }}
            </p>
            <div class="mt-3 grid gap-2 sm:grid-cols-2">
              <div
                class="rounded-lais-sm border border-lais-border/70 bg-white px-3 py-2"
              >
                <p
                  class="text-[11px] font-semibold uppercase text-lais-text-muted"
                >
                  {{ t('SURVEYS.FRIENDLY_LINK.DELIVERY_STATUS_LABEL') }}
                </p>
                <div class="mt-1 flex flex-wrap items-center gap-2">
                  <span
                    class="rounded-full px-2 py-1 text-[11px] font-semibold"
                    :class="
                      deliveryStatusClass(deliveryDetails(generatedLink).status)
                    "
                  >
                    {{
                      deliveryStatusLabel(deliveryDetails(generatedLink).status)
                    }}
                  </span>
                  <span class="text-[12px] text-lais-text-helper">
                    {{
                      deliveryChannelLabel(
                        deliveryDetails(generatedLink).channel
                      )
                    }}
                  </span>
                </div>
              </div>
              <div
                class="rounded-lais-sm border border-lais-border/70 bg-white px-3 py-2"
              >
                <p
                  class="text-[11px] font-semibold uppercase text-lais-text-muted"
                >
                  {{ t('SURVEYS.FRIENDLY_LINK.DELIVERY_UPDATED_AT') }}
                </p>
                <p class="mt-1 text-[12px] text-lais-text-helper">
                  {{ formatDateTime(deliveryDetails(generatedLink).updatedAt) }}
                </p>
              </div>
            </div>
            <p
              v-if="deliveryDetails(generatedLink).reason"
              class="mt-2 rounded-lais-sm border border-lais-danger/20 bg-white px-3 py-2 text-[12px] text-lais-danger"
              role="note"
            >
              <span class="font-semibold">
                {{ t('SURVEYS.FRIENDLY_LINK.DELIVERY_REASON') }}
              </span>
              {{ deliveryReasonLabel(deliveryDetails(generatedLink).reason) }}
            </p>
            <LaisPrimaryButton
              v-if="supportsDeliveryRetry(generatedLink)"
              class="mt-3"
              size="sm"
              variant="ghost"
              icon="i-lucide-refresh-cw"
              :is-loading="isGenerating"
              :disabled="!canGenerate"
              @click="retryGeneratedDelivery"
            >
              {{
                t('SURVEYS.FRIENDLY_LINK.RETRY_NEW_LINK', {
                  channel: deliveryChannelLabel(
                    deliveryDetails(generatedLink).channel
                  ),
                })
              }}
            </LaisPrimaryButton>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <a
              v-if="generatedLink.url"
              :href="generatedLink.url"
              target="_blank"
              rel="noopener noreferrer"
              class="grid size-9 place-items-center rounded-lais border border-lais-border bg-white text-lais-primary hover:border-lais-primary"
              :aria-label="t('SURVEYS.FRIENDLY_LINK.OPEN')"
            >
              <span class="i-lucide-external-link size-4" aria-hidden="true" />
            </a>
            <button
              type="button"
              class="grid size-9 place-items-center rounded-lais border border-lais-border bg-white text-lais-primary hover:border-lais-primary"
              :aria-label="t('SURVEYS.FRIENDLY_LINK.COPY')"
              @click="copy(generatedLink.url)"
            >
              <span class="i-lucide-copy size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      <section class="grid gap-3">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-lais-body-sm font-semibold text-lais-text">
              {{ t('SURVEYS.FRIENDLY_LINK.HISTORY_TITLE') }}
            </h3>
            <p class="mt-0.5 text-[12px] text-lais-text-helper">
              {{ t('SURVEYS.FRIENDLY_LINK.HISTORY_DESCRIPTION') }}
            </p>
          </div>
          <span
            v-if="isLoadingLinks"
            class="i-lucide-loader-circle size-4 animate-spin text-lais-text-muted"
            :aria-label="t('SURVEYS.FRIENDLY_LINK.LOADING')"
          />
        </div>
        <div
          v-if="isLoadingLinks && !links.length"
          class="flex items-center justify-center gap-2 rounded-lais border border-dashed border-lais-border px-4 py-6 text-center text-lais-body-sm text-lais-text-helper"
          role="status"
          aria-live="polite"
        >
          <span
            class="i-lucide-loader-circle size-4 animate-spin"
            aria-hidden="true"
          />
          {{ t('SURVEYS.FRIENDLY_LINK.LOADING') }}
        </div>
        <div
          v-else-if="historyError"
          class="flex flex-wrap items-center justify-between gap-3 rounded-lais border border-lais-danger/25 bg-lais-danger-50/50 px-4 py-4"
          role="alert"
          aria-live="assertive"
        >
          <div
            class="flex items-start gap-2 text-lais-body-sm text-lais-danger"
          >
            <span
              class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <span>{{ historyError }}</span>
          </div>
          <LaisPrimaryButton
            size="sm"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            :is-loading="isLoadingLinks"
            @click="fetchLinks"
          >
            {{ t('SURVEYS.FRIENDLY_LINK.RETRY_HISTORY') }}
          </LaisPrimaryButton>
        </div>
        <div
          v-else-if="!links.length"
          class="rounded-lais border border-dashed border-lais-border px-4 py-6 text-center text-lais-body-sm text-lais-text-helper"
        >
          {{ t('SURVEYS.FRIENDLY_LINK.HISTORY_EMPTY') }}
        </div>
        <div
          v-else
          class="overflow-hidden rounded-lais border border-lais-border"
        >
          <div
            v-for="link in links"
            :key="link.id"
            class="flex flex-wrap items-center justify-between gap-3 border-b border-lais-border px-3 py-3 last:border-0 sm:px-4"
          >
            <div class="min-w-0">
              <p
                class="truncate text-lais-body-sm font-semibold text-lais-text"
              >
                {{ link.contact?.name || link.customer_code }}
              </p>
              <p class="truncate text-[12px] text-lais-text-helper">
                {{ link.imovel?.label || link.property_code }}
                <span aria-hidden="true">
                  {{ t('SURVEYS.FRIENDLY_LINK.META_SEPARATOR') }}
                </span>
                {{ formatDateTime(link.expires_at) }}
              </p>
              <div class="mt-2 flex flex-wrap items-center gap-2">
                <span
                  class="rounded-full px-2 py-1 text-[11px] font-semibold"
                  :class="deliveryStatusClass(deliveryDetails(link).status)"
                >
                  {{ deliveryStatusLabel(deliveryDetails(link).status) }}
                </span>
                <span class="text-[12px] text-lais-text-helper">
                  {{ deliveryChannelLabel(deliveryDetails(link).channel) }}
                </span>
                <span
                  v-if="deliveryDetails(link).updatedAt"
                  class="text-[12px] text-lais-text-helper"
                >
                  <span aria-hidden="true">
                    {{ t('SURVEYS.FRIENDLY_LINK.META_SEPARATOR') }}
                  </span>
                  {{ formatDateTime(deliveryDetails(link).updatedAt) }}
                </span>
              </div>
              <p
                v-if="deliveryDetails(link).reason"
                class="mt-1 text-[12px] text-lais-danger"
                role="note"
              >
                <span class="font-semibold">
                  {{ t('SURVEYS.FRIENDLY_LINK.DELIVERY_REASON') }}
                </span>
                {{ deliveryReasonLabel(deliveryDetails(link).reason) }}
              </p>
            </div>
            <div
              class="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto"
            >
              <span
                class="rounded-full px-2 py-1 text-[11px] font-semibold"
                :class="statusClass(link.status)"
              >
                {{ statusLabel(link.status) }}
              </span>
              <button
                v-if="link.status === 'active'"
                type="button"
                class="text-[12px] font-semibold text-lais-danger hover:underline"
                :disabled="revokingLinkId === link.id"
                :aria-busy="revokingLinkId === link.id ? 'true' : undefined"
                @click="askRevoke(link)"
              >
                {{ t('SURVEYS.FRIENDLY_LINK.REVOKE') }}
              </button>
              <button
                v-if="supportsDeliveryRetry(link)"
                type="button"
                class="text-[12px] font-semibold text-lais-primary hover:underline"
                @click="prepareReissue(link)"
              >
                {{
                  t('SURVEYS.FRIENDLY_LINK.REISSUE', {
                    channel: deliveryChannelLabel(
                      deliveryDetails(link).channel
                    ),
                  })
                }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </LaisModal>

  <LaisConfirmModal
    :show="Boolean(linkPendingRevocation)"
    :title="t('SURVEYS.FRIENDLY_LINK.REVOKE_CONFIRM_TITLE')"
    :description="
      t('SURVEYS.FRIENDLY_LINK.REVOKE_CONFIRM_DESCRIPTION', {
        contact: linkPendingRevocation?.contact?.name || '',
      })
    "
    :confirm-text="t('SURVEYS.FRIENDLY_LINK.REVOKE_CONFIRM')"
    :cancel-text="t('SURVEYS.FRIENDLY_LINK.REVOKE_CANCEL')"
    :is-loading="Boolean(revokingLinkId)"
    icon="i-lucide-shield-alert"
    icon-class="bg-lais-danger-50 text-lais-danger"
    @close="closeRevoke"
    @confirm="revoke"
  >
    <p
      v-if="revokeError"
      class="mt-3 rounded-lais-sm border border-lais-danger/20 bg-lais-danger-50 px-3 py-2 text-lais-body-sm text-lais-danger"
      role="alert"
      aria-live="assertive"
    >
      {{ revokeError }}
    </p>
  </LaisConfirmModal>
</template>
